import { inject, Injectable } from '@angular/core';
import { Client, UserDeviceRequest } from '@api/client';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { AuthService } from '@services/auth.service';
import { filter, firstValueFrom, merge, Subject, takeUntil } from 'rxjs';

@Injectable({providedIn: 'root'})
export class PushRegistrationService {
  private readonly client = inject(Client);
  private readonly authService = inject(AuthService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly activeRegistrations = new Map<1 | 2, object>();
  private readonly registrationChanged = new Subject<void>();

  public async register(token: string, type: 1 | 2, isActive: () => boolean = (): boolean => true): Promise<number | null> {
    const actorId = this.authService.parsedToken?.userId;
    const version = this.authService.sessionVersion;
    if (!token || !actorId || !isActive()) return null;
    const registration = {};
    this.activeRegistrations.set(type, registration);
    this.registrationChanged.next();
    const isCurrent = (): boolean => isActive() && this.authService.parsedToken?.userId === actorId
      && this.authService.sessionVersion === version && this.activeRegistrations.get(type) === registration;

    const name = await this.phoneInfoService.getDeviceModelWithOS();
    if (!isCurrent()) return null;

    // A newer token must cancel the old channel's offline/refresh wait, not the other channel.
    const response = await firstValueFrom(this.client.userDevicePost(new UserDeviceRequest({token, type, name})).pipe(
      takeUntil(merge(this.authService.parsedToken$, this.registrationChanged).pipe(filter((): boolean => !isCurrent()))),
    ), {defaultValue: null});
    if (!response || !isCurrent()) return null;
    if (response.data?.user_id !== actorId || !Number.isInteger(response.data?.id) || response.data.id <= 0) {
      throw new Error('Push registration response identity mismatch');
    }
    return response.data.id;
  }
}
